<template>
  <div class="pf">
    <!-- Header -->
    <header class="pf__head">
      <div class="pf__head-title">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="4" y1="12" x2="14" y2="12"/>
          <line x1="4" y1="18" x2="20" y2="18"/>
        </svg>
        Фильтры
        <span v-if="hasActiveFilters" class="pf__count-badge">{{ totalActiveCount }}</span>
      </div>
      <button v-if="hasActiveFilters" type="button" class="pf__clear" @click="$emit('clear')">
        Сбросить
      </button>
    </header>

    <!-- Active-filter pill strip -->
    <div v-if="hasActiveFilters && activePills.length" class="pf__active-pills">
      <button
        v-for="pill in activePills"
        :key="pill.key + '-' + pill.id"
        type="button"
        class="pf__pill"
        @click="$emit('remove-pill', pill)"
        :title="'Убрать: ' + pill.label"
      >
        {{ pill.label }}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <template v-if="ui">
      <!-- Chip sections -->
      <FilterChipSection
        v-if="ui.study_degrees?.length"
        label="Степень"
        :options="ui.study_degrees"
        :selected="filters.studyDegreeIds"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'studyDegreeIds', id })"
      />

      <FilterChipSection
        v-if="ui.scholarships?.length"
        label="Стипендия"
        :options="ui.scholarships"
        :selected="filters.scholarshipIds"
        :initial-max="5"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'scholarshipIds', id })"
      />

      <FilterChipSection
        v-if="ui.categories?.length"
        label="Категория"
        :options="ui.categories"
        :selected="filters.categoryIds"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'categoryIds', id })"
      />

      <FilterChipSection
        v-if="ui.subcategories?.length && filters.categoryIds.length"
        label="Подкатегория"
        :options="ui.subcategories"
        :selected="filters.subcategoryIds"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'subcategoryIds', id })"
      />

      <FilterChipSection
        v-if="ui.countries?.length"
        label="Страна"
        :options="ui.countries"
        :selected="filters.countryIds"
        :initial-max="6"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'countryIds', id })"
      />

      <FilterChipSection
        v-if="ui.states?.length && filters.countryIds.length"
        label="Штат / регион"
        :options="ui.states"
        :selected="filters.stateIds"
        :default-open="true"
        @toggle="(id) => $emit('toggle-multi', { key: 'stateIds', id })"
      />

      <!-- Sliders -->
      <div class="pf__sliders">
        <div class="pf__slider-block">
          <div class="pf__slider-labels">
            <span>GPA</span>
            <span class="pf__slider-value">{{ gpaLabel }}</span>
          </div>
          <rangeUi
            :min="gpaSlider.min"
            :max="gpaSlider.max"
            :step="0.1"
            :range="[gpaSlider.from, gpaSlider.to]"
            value=""
            name="psp-gpa"
            @updatemin="(v) => onGpa(v, gpaSlider.to)"
            @updatemax="(v) => onGpa(gpaSlider.from, v)"
          />
        </div>

        <div class="pf__slider-block">
          <div class="pf__slider-labels">
            <span>IELTS</span>
            <span class="pf__slider-value">{{ ieltsLabel }}</span>
          </div>
          <rangeUi
            :min="ieltsSlider.min"
            :max="ieltsSlider.max"
            :step="0.5"
            :range="[ieltsSlider.from, ieltsSlider.to]"
            value=""
            name="psp-ielts"
            @updatemin="(v) => onIelts(v, ieltsSlider.to)"
            @updatemax="(v) => onIelts(ieltsSlider.from, v)"
          />
        </div>
      </div>

      <!-- Duolingo -->
      <div v-if="ui.duolingo" class="pf__section">
        <p class="pf__section-label">Duolingo</p>
        <div class="pf__chips">
          <button type="button" class="pf-chip" :class="{ 'pf-chip--active': filters.duolingo === null }" @click="$emit('duolingo', null)">
            Любой
          </button>
          <button type="button" class="pf-chip"
            :class="{ 'pf-chip--active': filters.duolingo === true, 'pf-chip--disabled': !ui.duolingo.true }"
            :disabled="!ui.duolingo.true && filters.duolingo !== true"
            @click="$emit('duolingo', true)"
          >
            Да <span v-if="filters.duolingo !== true" class="pf-chip__count">{{ ui.duolingo.true }}</span>
          </button>
          <button type="button" class="pf-chip"
            :class="{ 'pf-chip--active': filters.duolingo === false, 'pf-chip--disabled': !ui.duolingo.false }"
            :disabled="!ui.duolingo.false && filters.duolingo !== false"
            @click="$emit('duolingo', false)"
          >
            Нет <span v-if="filters.duolingo !== false" class="pf-chip__count">{{ ui.duolingo.false }}</span>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="pf__loading">
      <svg class="pf__loading-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
        <path d="M22 12a10 10 0 0 1-10 10"/>
      </svg>
      Загрузка фильтров…
    </div>
  </div>
</template>

<script>
import rangeUi from '@/components/rangeUi.vue';
import FilterChipSection from '@/components/programs/FilterChipSection.vue';

export default {
  name: 'ProgramsFilterPanel',
  components: { rangeUi, FilterChipSection },
  props: {
    ui:               { type: Object,  default: null },
    filters:          { type: Object,  required: true },
    gpaSlider:        { type: Object,  required: true },
    ieltsSlider:      { type: Object,  required: true },
    hasActiveFilters: { type: Boolean, default: false },
  },
  emits: ['toggle-multi', 'gpa-change', 'ielts-change', 'duolingo', 'clear', 'remove-pill'],
  computed: {
    gpaLabel() {
      const r = this.ui?.gpaRange;
      if (!r || (this.gpaSlider.from <= r.min && this.gpaSlider.to >= r.max)) return 'Любой';
      return `${this.gpaSlider.from} – ${this.gpaSlider.to}`;
    },
    ieltsLabel() {
      const r = this.ui?.ieltsRange;
      if (!r || (this.ieltsSlider.from <= r.min && this.ieltsSlider.to >= r.max)) return 'Любой';
      return `${this.ieltsSlider.from} – ${this.ieltsSlider.to}`;
    },
    totalActiveCount() {
      let n = 0;
      const f = this.filters;
      n += (f.studyDegreeIds?.length || 0);
      n += (f.categoryIds?.length || 0);
      n += (f.subcategoryIds?.length || 0);
      n += (f.countryIds?.length || 0);
      n += (f.stateIds?.length || 0);
      n += (f.scholarshipIds?.length || 0);
      n += (f.rankIds?.length || 0);
      if (f.duolingo !== null) n++;
      return n;
    },
    activePills() {
      if (!this.ui) return [];
      const pills = [];
      const add = (arr, key, source) => {
        (arr || []).forEach(id => {
          const opt = (this.ui[source] || []).find(o => o.id === id);
          if (opt) pills.push({ key, id, label: opt.name });
        });
      };
      add(this.filters.studyDegreeIds, 'studyDegreeIds', 'study_degrees');
      add(this.filters.categoryIds,    'categoryIds',    'categories');
      add(this.filters.subcategoryIds, 'subcategoryIds', 'subcategories');
      add(this.filters.countryIds,     'countryIds',     'countries');
      add(this.filters.stateIds,       'stateIds',       'states');
      add(this.filters.scholarshipIds, 'scholarshipIds', 'scholarships');
      add(this.filters.rankIds,        'rankIds',        'ranks');
      return pills;
    },
  },
  methods: {
    onGpa(from, to)   { this.$emit('gpa-change',   { from, to }); },
    onIelts(from, to) { this.$emit('ielts-change', { from, to }); },
  },
};
</script>

<style lang="scss" scoped>
.pf {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── header ────────────────────────────── */
.pf__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f1f3;
  margin-bottom: 4px;
}

.pf__head-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 800;
  color: #111827;
}

.pf__count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8192c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.pf__clear {
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 700;
  color: #e8192c;
  cursor: pointer;
  font-family: inherit;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.12s;
  &:hover { background: rgba(232, 25, 44, 0.06); }
}

/* ── active pills strip ────────────────── */
.pf__active-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 0 6px;
  border-bottom: 1px solid #f0f1f3;
  margin-bottom: 4px;
}

.pf__pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1.5px solid rgba(232, 25, 44, 0.35);
  background: rgba(232, 25, 44, 0.06);
  color: #e8192c;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: rgba(232, 25, 44, 0.12);
    border-color: #e8192c;
  }

  svg { opacity: 0.75; }
}

/* ── sliders ───────────────────────────── */
.pf__sliders {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 14px 0 4px;
  border-top: 1px solid #f0f1f3;
  margin-top: 4px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
}

.pf__slider-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pf__slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.pf__slider-value {
  font-weight: 700;
  color: #111827;
}

/* ── duolingo section ──────────────────── */
.pf__section {
  padding-top: 14px;
  border-top: 1px solid #f0f1f3;
  margin-top: 4px;
}

.pf__section-label {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151;
}

.pf__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pf-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1.5px solid #e5e7eb;
  background: transparent;
  color: #6b7280;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &__count {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
  }

  &:hover:not(:disabled) {
    border-color: #d1d5db;
    color: #374151;
  }

  &--active {
    border-color: #e8192c;
    background: rgba(232, 25, 44, 0.06);
    color: #e8192c;
    .pf-chip__count { color: #e8192c; }
  }

  &--disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
}

/* ── loading ───────────────────────────── */
.pf__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #9ca3af;
  padding: 16px 0;
}

.pf__loading-spin {
  animation: pf-spin 0.9s linear infinite;
}

@keyframes pf-spin {
  to { transform: rotate(360deg); }
}
</style>
